import axios from 'axios';
import {useToNumber, useCheckType, types} from '@sttedra_ui/framework';
import BaseApiModel from '../../_commons/base.api-model';

/**
 * Regex para sustituir las cadenas que contengan los símbolos
 * "/{", "|{", "@{" o ":{" y que les procedan la palabra y se cierre con
 * el símbolo "}"
 *
 * @example /{palabra}
 * @example ={palabra}
 * @example @{palabra}
 * @example :{palabra}
 */
const REGEX_BRACKETS_WITH_SIMBOL = /[\/\|@:=]{(\w+)}/g;

/**
  * Regex de inicio, puede contener los simbolos: "/{", "|{" o "@{"
  *
  * @example /{
  * @example ={
  * @example @{
  * @example :{
  */
const REGEX_INDEX_BRACKETS = /[\/\|@:=]{/g;

export default class MapBase extends BaseApiModel {
  // #region Props
  name;
  description;
  default;
  asLayer;
  zoomMax;
  legend;
  request;
  alternativeRequest;
  getInfo;
  // #endregion


  constructor(data) {
    super(data);

    const {
      name,
      description,
      asLayer,
      default: visible,
      zoomMax,
      legend,
      request,
      alternativeRequest,
      getInfo,
    } = data;

    this.name = this.#getString(name);
    this.description = this.#getString(description);
    this.asLayer = this.#getBoolean(asLayer);
    this.default = this.#getBoolean(visible);
    this.zoomMax = this.#getNumber(zoomMax);
    this.legend = this.#getBoolean(legend);
    this.getInfo = this.#getBoolean(getInfo);
    this.request = request;
    this.alternativeRequest = alternativeRequest;
  }


  // #region Public methods
  /**
   * Replace the values in string
   *
   * @param {String} str
   * @param {Array} arr Array to replace
   *
   * @return {String}
   */
  replaceString(arr, str = '') {
    let newStr = str;
    if (newStr && typeof(newStr === 'String')) {
      // Busca todo símbolo que contenga la expresión regular
      const matchs = newStr.match(REGEX_BRACKETS_WITH_SIMBOL);
      if (matchs) {
        for (const match of matchs) {
          let newString = match.replace(REGEX_INDEX_BRACKETS, '');
          newString = newString.replace('}', '');
          const newValue = arr[newString] ? String(arr[newString]) : null;

          if (newValue) {
            // Sustituye todo valor indicado con llaves
            newStr = newStr.replace(
              `{${newString}}`, newValue,
            );
          }
        }
      }
    }
    return newStr;
  }

  /**
   * Return the UrlCapabilities of map
   *
   * @return {String}
   */
  getUrlCapabilities() {
    const newUrlCapabilities = this.replaceString(
      this.request,
      this.request.urlCapabilities,
    );
    return this.request.host + (newUrlCapabilities ?? '');
  }

  /**
   * Return the UrlRequest of map
   *
   * @return {String}
   */
  getUrlRequest() {
    const newUrlRequest = this.replaceString(
      this.request,
      this.request.urlRequest,
    );
    return this.request.host + (newUrlRequest ?? '');
  }

  /**
   * Return the UrlCapabilitiesAlternative of map if exists
   *
   * @return {String}
   */
  getUrlCapabilitiesAlternative() {
    if (!this.alternativeRequest) {
      return null;
    }

    const newUrlCapabilities = this.replaceString(
      this.alternativeRequest,
      this.alternativeRequest.urlCapabilities,
    );

    return this.alternativeRequest.host + (newUrlCapabilities ?? '');
  }

  /**
   * Return the UrlRequestAlternative of map if exists
   *
   * @return {String}
   */
  getUrlRequestAlternative() {
    if (!this.alternativeRequest) {
      return null;
    }

    const newUrlRequest = this.replaceString(
      this.alternativeRequest,
      this.alternativeRequest.urlRequest,
    );

    return this.alternativeRequest.host + (newUrlRequest ?? '');
  }

  getUrlLegend() {
    if (!this.request?.urlLegend || !this.legend) {
      return null;
    }

    const newUrlLegend = this.replaceString(
      this.request,
      this.request.urlLegend,
    );
    return this.request.host + (newUrlLegend ?? '');
  }

  async getUrlInfo({
    resolution,
    zoom,
    source,
    coordinate,
    format = 'application/json',
  }) {
    let url = null;
    const {request} = this;
    if (request?.protocol.toLowerCase() === 'wmts') {
      const tileSize = source.getTileGrid().getTileSize(zoom);
      const tileOrigin = source.getTileGrid().getOrigin(zoom);

      const fx = (coordinate[0] - tileOrigin[0]) / (resolution * tileSize);
      const fy = (tileOrigin[1] - coordinate[1]) / (resolution * tileSize);
      const col = Math.floor(fx);
      const row = Math.floor(fy);
      const tileI = Math.floor((fx - col) * tileSize);
      const tileJ = Math.floor((fy - row) * tileSize);

      const object = {
        ...request,
        matrixIds: zoom,
        row,
        col,
        tileI,
        tileJ,
        style: 'null',
        protocol: request.protocol.toLowerCase(),
      };
      const urlInfo = this.replaceString(
        object,
        this.request.urlGetInfo,
      );
      url = `${request.host}${urlInfo}`;
    } else {
      url = source.getFeatureInfoUrl(coordinate, resolution, 'EPSG:25830', {
        FORMAT: format,
        INFO_FORMAT: format,
      });
    }

    try {
      const {data} = await axios.get(url);
      return data;
    } catch (error) {
      console.error(error);
    }
    return null;
  }
  // #endregion


  // #region Private methods
  #getNumber(newValue) {
    const number = useToNumber(newValue);
    return number >= 0 ? number : null;
  }
  #getString(newValue) {
    const expectedType = useCheckType(newValue, types.STRING);
    return expectedType ? newValue : null;
  }
  #getBoolean(newValue) {
    switch (typeof newValue) {
      case 'this.request.urlCapabilities':
        return /^(true|false)$/gmi.test(newValue) ? JSON.parse(newValue) : null;
      case 'boolean':
        return newValue;
      default:
        return null;
    }
  }
  // #endregion
}

